import type { Region } from '@/lib/demo/onboarding-profile';

/**
 * Demo region resolution (BUG-88).
 *
 * The demo region used to live ONLY in sessionStorage, which is per-tab and dies
 * when the tab closes. Demo mode itself is mirrored to localStorage
 * (`alisio-demo-mode`), and the MX demo's language is persisted to
 * localStorage `app-settings.language` — both DURABLE. The region was the odd
 * one out, so a returning tab kept demo mode + Spanish but silently lost the
 * region and fell back to 'us'. That mismatch is what rendered the English
 * onboarding on top of the Spanish Mexican demo.
 *
 * Region is resolved from the most specific durable signal available.
 */

const SESSION_KEY = 'demo_region';
const LOCAL_KEY = 'alisio-demo-region';

function readLanguage(): string | undefined {
  try {
    return JSON.parse(localStorage.getItem('app-settings') || '{}').language;
  } catch {
    return undefined;
  }
}

/**
 * The demo region for this browser, in precedence order:
 *   1. sessionStorage — this tab's active demo session
 *   2. localStorage mirror — a demo started earlier in another tab
 *   3. the app language — 'es' only ever belongs to the MX demo tenant
 *   4. 'us'
 */
export function getDemoRegion(): Region {
  const fromSession = sessionStorage.getItem(SESSION_KEY);
  if (fromSession === 'mx' || fromSession === 'us') return fromSession;

  const fromLocal = localStorage.getItem(LOCAL_KEY);
  if (fromLocal === 'mx' || fromLocal === 'us') return fromLocal;

  return readLanguage() === 'es' ? 'mx' : 'us';
}

/** Persist the region to both stores so it survives a new tab. */
export function setDemoRegion(region: Region): void {
  sessionStorage.setItem(SESSION_KEY, region);
  localStorage.setItem(LOCAL_KEY, region);
}

/** Clear both stores — called on sign-out alongside the other demo keys. */
export function clearDemoRegion(): void {
  sessionStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(LOCAL_KEY);
}
